import { Button, DatePicker, Form, Input, Modal, Space, message } from "antd";
import { useState } from "react";
import { transportApi } from "../../api/transportApi";
import { getApiErrorMessage } from "../../utils/apiError";
import { getDefaultContext } from "../../api/httpClient";
import { getEnteredByUser } from "../../utils/currentUser";
import type { LookupItem } from "./LookupModal";

interface DriverCreateModalProps {
  open: boolean;
  prefillName?: string;
  onClose: () => void;
  onCreated: (item: LookupItem) => void;
}

export const DriverCreateModal = ({
  open,
  prefillName,
  onClose,
  onCreated,
}: DriverCreateModalProps) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleAfterOpen = (visible: boolean) => {
    if (visible) {
      form.resetFields();
      if (prefillName) form.setFieldValue("driverName", prefillName);
    }
  };

  const handleSave = async () => {
    let values: any;
    try { values = await form.validateFields(); }
    catch { return; }

    const ctx = getDefaultContext();
    setSaving(true);
    try {
      const created = await transportApi.createDriver({
        ...ctx,
        enteredBy: getEnteredByUser(),
        driverName: values.driverName,
        mobile: values.mobile ?? "",
        licenseNo: values.licenseNo ?? "",
        licenseExpiry: values.licenseExpiry
          ? (values.licenseExpiry as any).toISOString()
          : new Date(Date.now() + 365 * 86400000).toISOString(),
        address: values.address ?? "",
        isActive: true,
      });
      const item: LookupItem = {
        id: created.id,
        label: created.driverName,
        subtitle: created.mobile || created.licenseNo || undefined,
      };
      message.success(`Driver "${created.driverName}" created`);
      onCreated(item);
      form.resetFields();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to create driver"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Add New Driver"
      open={open}
      onCancel={onClose}
      afterOpenChange={handleAfterOpen}
      destroyOnClose
      width={480}
      footer={
        <Space style={{ justifyContent: "flex-end", width: "100%" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={() => void handleSave()}>
            Save Driver
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
        <Form.Item
          name="driverName"
          label="Driver Name"
          rules={[{ required: true, message: "Enter driver name" }]}
        >
          <Input placeholder="e.g. Ramesh Kumar" autoFocus />
        </Form.Item>
        <Form.Item name="mobile" label="Mobile No">
          <Input placeholder="e.g. 9876543210" />
        </Form.Item>
        <Form.Item name="licenseNo" label="License No">
          <Input placeholder="e.g. DL-0119850001234"
            style={{ textTransform: "uppercase" }}
            onChange={e => form.setFieldValue("licenseNo", e.target.value.toUpperCase())} />
        </Form.Item>
        <Form.Item name="licenseExpiry" label="License Expiry">
          <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item name="address" label="Address">
          <Input.TextArea rows={2} placeholder="Residential address" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
